import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Camera, MousePointerClick, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const INTRO_SEEN_KEY = 'my360.intro.seen';

interface Slide {
  icon: React.ElementType;
  /** Tailwind classes for the icon halo gradient. */
  haloClass: string;
}

const slides: Slide[] = [
  {
    icon: Camera,
    haloClass: 'from-[var(--color-primary)] to-[var(--color-accent)]',
  },
  {
    icon: MousePointerClick,
    haloClass: 'from-[var(--color-accent)] to-emerald-400',
  },
  {
    icon: BarChart3,
    haloClass: 'from-rose-500 to-[var(--color-primary)]',
  },
];

export function markIntroSeen() {
  try {
    localStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch {
    /* noop */
  }
}

export function hasSeenIntro(): boolean {
  try {
    return localStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

export default function MobileIntro() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const isLast = index === slides.length - 1;
  const slide = slides[index];
  const Icon = slide.icon;
  const slideTexts = t('mobileIntro:slides', { returnObjects: true });

  const handleNext = () => {
    if (isLast) {
      markIntroSeen();
      navigate({ to: '/login' });
    } else {
      setIndex((i) => i + 1);
    }
  };

  const handleSkip = () => {
    markIntroSeen();
    navigate({ to: '/login' });
  };

  return (
    <div className="flex h-[100dvh] flex-col bg-[var(--color-base)] text-[var(--color-text)] overflow-hidden">
      {/* Top bar — Skip + dots */}
      <div className="flex items-center justify-between px-5 pt-[max(var(--inset-top-safe),20px)] pb-2 shrink-0">
        <button
          onClick={handleSkip}
          className="text-xs font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
        >
          {t('mobileIntro:skip')}
        </button>
        <div className="flex items-center gap-1.5">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index
                  ? 'w-6 bg-[var(--color-primary)]'
                  : 'w-1.5 bg-[var(--color-text-muted)]/30'
              }`}
            />
          ))}
        </div>
        <div className="w-8" /> {/* spacer for symmetry */}
      </div>

      {/* Slide */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            className="flex flex-col items-center text-center max-w-md"
          >
            {/* Icon with gradient halo */}
            <div className="relative mb-10">
              <div
                className={`absolute -inset-6 rounded-full bg-gradient-to-br ${slide.haloClass} opacity-30 blur-3xl pointer-events-none`}
              />
              <div
                className={`relative h-24 w-24 rounded-3xl bg-gradient-to-br ${slide.haloClass} flex items-center justify-center shadow-[0_12px_40px_-8px_rgba(0,102,255,0.55)]`}
              >
                <Icon className="h-12 w-12 text-white" strokeWidth={1.5} />
              </div>
            </div>

            <h1 className="text-2xl font-bold tracking-tight mb-3 leading-tight">
              {slideTexts[index].title}
            </h1>
            <p className="text-[15px] text-[var(--color-text-muted)] leading-relaxed">
              {slideTexts[index].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* CTA */}
      <div className="px-6 pb-[max(env(safe-area-inset-bottom),24px)] pt-2 shrink-0">
        <Button
          variant="primary"
          className="w-full h-12 text-sm font-semibold"
          onClick={handleNext}
        >
          {isLast ? t('mobileIntro:cta.start') : t('mobileIntro:cta.next')}
          <ChevronRight className="ml-1.5 h-4 w-4" />
        </Button>
        {isLast && (
          <p className="mt-3 text-center text-[11px] text-[var(--color-text-muted)]">
            {t('mobileIntro:hasAccount')}{' '}
            <button
              onClick={() => {
                markIntroSeen();
                navigate({ to: '/login' });
              }}
              className="text-[var(--color-primary)] font-semibold underline-offset-2 hover:underline"
            >
              {t('mobileIntro:signIn')}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
